export type CateringGuideStep = {
  id: string;
  area: 'Preparazione' | 'Eventi' | 'Approvvigionamenti' | 'DDT e spedizioni' | 'Produzione e scarico';
  title: string;
  body: string;
  result: string;
};

export const CATERING_GUIDE_PREREQUISITES = [
  'Alimenti e lotti caricati in Magazzino, preferibilmente tramite DDT o fattura.',
  'Ricette con dosi per porzione e unità di misura coerenti con gli alimenti.',
  'Costi di personale, evento e struttura da usare nel preventivo.',
] as const;

export const CATERING_GUIDE_STEPS: readonly CateringGuideStep[] = [
  {
    id: 'prepare-menu',
    area: 'Preparazione',
    title: 'Prepara i piatti del menu',
    body: 'In Ricette collega ogni piatto agli alimenti di magazzino e indica dosi, unità e resa. Nel preventivo scegli le porzioni per ospite: 1 per tutti, 0,5 per metà degli ospiti o mezza porzione. Coperti e coefficiente alimentano sia i costi sia le quantità da acquistare.',
    result: 'Ottieni un menu calcolabile, collegato a ingredienti e costi reali.',
  },
  {
    id: 'create-quote',
    area: 'Eventi',
    title: 'Crea il preventivo dell’evento',
    body: 'Apri “Nuovo preventivo / evento”: si apre una pagina dedicata, senza popup. Segui le quattro sezioni Evento e ospiti, Menu e porzioni, Costi e prezzo, Riepilogo. Puoi passare liberamente tra le sezioni; su PC il riepilogo economico resta a lato. Le impostazioni dello storico prezzi si aprono nella prima sezione. Controlla i dati e salva dal riepilogo finale: cambiare sezione non salva il preventivo.',
    result: 'Ottieni costo previsto, scenari di margine e prezzo obiettivo per ospite.',
  },
  {
    id: 'revise-confirm',
    area: 'Eventi',
    title: 'Revisiona e conferma',
    body: 'Nell’agenda “Da gestire” trovi anche gli eventi passati ancora aperti. Usa “Modifica preventivo e coperti” per creare una revisione, poi conferma l’evento quando il cliente accetta. Prima degli acquisti aggiorna i coperti effettivamente attesi; avvia la produzione solo dopo avere verificato il menu. Da quel momento la modifica del preventivo non è più disponibile nella schermata.',
    result: 'Mantieni lo storico del preventivo senza sovrascrivere le revisioni precedenti.',
  },
  {
    id: 'procurement',
    area: 'Approvvigionamenti',
    title: 'Controlla cosa acquistare',
    body: 'Seleziona la settimana: il sistema somma i fabbisogni degli eventi confermati o in produzione e li confronta con le giacenze. Verifica anche gli ordini già inviati, i tempi di consegna, le confezioni del fornitore e gli impegni per eventi fuori dal periodo: non sono sottratti automaticamente.',
    result: 'Ottieni un suggerimento settimanale degli acquisti da valutare, non un ordine già emesso.',
  },
  {
    id: 'link-documents',
    area: 'DDT e spedizioni',
    title: 'Collega DDT e logistica',
    body: 'Apri l’evento e verifica i DDT suggeriti nei sette giorni precedenti. Per ogni documento puoi leggere fornitore, numero, data, alimenti e lotti disponibili; puoi selezionare anche altri documenti e un’eventuale spedizione verso la sala.',
    result: 'Ottieni il legame comprensibile tra evento, fornitore, documento di carico e lotti.',
  },
  {
    id: 'prepare-discharge',
    area: 'Produzione e scarico',
    title: 'Verifica la proposta di scarico',
    body: 'Il sistema esplode il menu per i coperti, calcola ogni ingrediente e propone i lotti FEFO, dando priorità ai DDT collegati. Controlla quantità, lotto, scadenza, fornitore e documento origine.',
    result: 'Ottieni quantità di scarico automatiche e tracciabili per ciascun alimento.',
  },
  {
    id: 'close-event',
    area: 'Produzione e scarico',
    title: 'Verifica il non utilizzato e chiudi',
    body: 'Nel campo “Non utilizzato” indica solo materia prima rimasta integra e disponibile nello stesso lotto. Non inserire piatti già preparati, scarti o eccedenze di buffet. Il consumato è la quantità allocata meno quella non utilizzata. Se la proposta non corrisponde a quantità e lotti effettivi, non confermarla.',
    result: 'Dopo la conferma ottieni lo scarico immutabile dei lotti indicati e le quantità non utilizzate, da confrontare con eventi simili senza modifiche automatiche alle ricette.',
  },
] as const;

export const CATERING_GUIDE_RESULTS = [
  'Preventivi revisionabili con scenari di costo, margine e prezzo obiettivo.',
  'Fabbisogni e suggerimenti settimanali di approvvigionamento.',
  'Tracciabilità evento → piatto → ingrediente → lotto → DDT → fornitore.',
  'Supporto sia agli eventi in sede sia alla merce spedita verso una sala.',
  'Consuntivo di scarico e materia prima non utilizzata, distinto dal rendiconto economico dell’evento.',
] as const;

export const CATERING_GUIDE_LIMITS = [
  'Collegare un DDT non scarica automaticamente la merce: lo scarico avviene solo alla conferma finale.',
  'Gli approvvigionamenti sono suggerimenti: TrackEAT non invia ordini ai fornitori.',
  'Dati ricetta o unità mancanti restano segnalati: il sistema non inventa quantità o costi.',
  'La stima manuale del menu richiede conferma esplicita e non sostituisce le ricette necessarie alla produzione.',
  'Il risultato economico mostrato è quello previsto dall’ultima revisione, non il margine effettivo a fine evento. Il confronto completo fra costi previsti e reali non è ancora disponibile.',
  'Il collegamento DDT non prenota la merce. Il prezzo storico e l’adeguamento prudenziale non garantiscono il prezzo futuro di mercato.',
] as const;
